import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  History,
  RefreshCw,
  Users,
} from "lucide-react";
import {
  CartesianGrid,
  ComposedChart,
  Area,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Button, Spinner } from "../../components/ui/index.ts";
import ScreenSkeleton from "../../components/ui/screen-skeleton/screen-skeleton.tsx";
import { formatCurrency } from "../../utils/currency.ts";
import type {
  ProjectActivityCategory,
  ProjectActivityEvent,
  ProjectTrackingReport,
} from "./types.ts";
import { useProjectActivity } from "./use-workstream-tracking.ts";
import { getProjectPhases, type ProjectPhaseView } from "./project-utilisation-overview.tsx";
import styles from "./project-activity-view.module.css";

const FILTERS: Array<{
  value: "ALL" | ProjectActivityCategory;
  label: string;
}> = [
  { value: "ALL", label: "All" },
  { value: "PHASES", label: "Phases" },
  { value: "PEOPLE", label: "People" },
  { value: "BURN", label: "Burn" },
  { value: "RISKS", label: "Risks" },
];

const CATEGORIES: ProjectActivityCategory[] = [
  "PHASES",
  "PEOPLE",
  "BURN",
  "RISKS",
];

type Props = {
  projectId: string;
  workstreamId: number | null;
  projectName: string;
  project: ProjectTrackingReport;
  projectStart: string | null;
  projectEnd: string | null;
  currency: string;
  includeShadow: boolean;
  includeResearch: boolean;
  includeGovernance: boolean;
  includeSalesCommission: boolean;
  onBack: () => void;
};

export function ProjectActivityView(props: Props) {
  const [filter, setFilter] = useState<"ALL" | ProjectActivityCategory>("ALL");
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const categories = useMemo(
    () => (filter === "ALL" ? CATEGORIES : [filter]),
    [filter],
  );

  const activity = useProjectActivity(
    props.projectId,
    props.workstreamId,
    props.includeShadow,
    props.includeResearch,
    props.includeGovernance,
    props.includeSalesCommission,
    categories,
  );

  const data = activity.data;
  const events = useMemo(() => deduplicateActivityEvents(data?.events ?? []), [data?.events]);
  const projectStart = props.projectStart ?? data?.projectStartDate ?? null;
  const projectEnd = props.projectEnd ?? data?.projectEndDate ?? null;
  const phases = useMemo(() => getProjectPhases(props.project), [props.project]);
  const criticalCount = events.filter((event) => event.severity === "CRITICAL").length;
  const warningCount = events.filter((event) => event.severity === "WARNING").length;

  const selectEvent = (id: number) => {
    setSelectedId(id);

    window.setTimeout(() => {
      document
        .getElementById(`activity-event-${id}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 0);
  };

  return (
    <section
      className={styles.activityView}
      aria-label={`${props.projectName} activity`}
    >
      <header className={styles.heading}>
        <div>
          <button type="button" className={styles.activityBack} onClick={props.onBack}><ArrowLeft /> Back</button>
          <span className={styles.headingIcon}>
            <History />
          </span>

          <div>
            <div className={styles.activityTitleRow}>
              <h2>Project Activity</h2>
              {criticalCount > 0 && <span className={styles.criticalCount}>{criticalCount} critical</span>}
              {warningCount > 0 && <span className={styles.warningCount}>{warningCount} warnings</span>}
              <span className={styles.scopeCount}>All phases · all inclusions</span>
            </div>
            <p>
              {projectStart && projectEnd
                ? `${dateOnly(projectStart)} – ${dateOnly(
                    projectEnd,
                  )}`
                : "What changed across delivery, people, burn, and risk."}
            </p>
          </div>
        </div>

        <div className={styles.filters} aria-label="Activity categories">
          {FILTERS.map((item) => (
            <button
              key={item.value}
              type="button"
              className={
                filter === item.value ? styles.filterActive : undefined
              }
              aria-pressed={filter === item.value}
              onClick={() => setFilter(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </header>

      {activity.loading && data && <span className={styles.refreshing} role="status">Updating project activity…</span>}

      {data?.partialData && (
        <div className={styles.partialNotice}>
          <AlertTriangle />
          <div>
            <strong>Some history is reconstructed</strong>
            <span>
              {data.partialDataReason ||
                "Earlier activity uses the best data currently available and may not reflect historical rate changes."}
            </span>
          </div>
        </div>
      )}

      {activity.error && data && (
        <div className={styles.inlineError}>
          <AlertTriangle />
          <span>{activity.error}</span>
          <button type="button" onClick={() => void activity.reload()}>
            Retry
          </button>
        </div>
      )}

      {activity.loading && !data ? (
        <ScreenSkeleton label="Loading project activity" variant="tracking" compact />
      ) : activity.error && !data ? (
        <State tone="error">
          <AlertTriangle />
          {activity.error}
          <Button size="sm" onClick={() => void activity.reload()}>
            Retry
          </Button>
        </State>
      ) : !events.length ? (
        <State>
          <History />
          No activity matches this scope yet.
        </State>
      ) : (
        <div
          className={`${styles.dashboard} ${
            activity.loading ? styles.dashboardLoading : ""
          }`}
          aria-busy={activity.loading}
        >
          <ActivityTimeline
            events={events}
            start={projectStart}
            end={projectEnd}
            today={data?.asOf}
            selectedId={selectedId}
            onSelect={selectEvent}
            phases={phases}
          />

          <SwimLanes
            events={events}
            start={projectStart}
            end={projectEnd}
            today={data?.asOf}
            selectedId={selectedId}
            onSelect={selectEvent}
          />

          <BurnTrend
            data={data!}
            currency={props.currency}
            events={events}
            selectedId={selectedId}
            onSelect={selectEvent}
            projectStart={projectStart}
            projectEnd={projectEnd}
          />

          <EventLog
            events={events}
            selectedId={selectedId}
            onSelect={setSelectedId}
            hasMore={Boolean(data?.hasMore)}
            loadingMore={activity.loadingMore}
            onLoadMore={() => void activity.loadMore()}
          />
        </div>
      )}
    </section>
  );
}

const PHASE_COLORS = ["#5d65df", "#7a3ff2", "#d68408", "#3da05a", "#456de8", "#7439ee"];

type TimelineScale = { minimum: number; maximum: number; today: number; ticks: Array<{ label: string; position: number }> };

function ActivityTimeline({ events, phases, start, end, today, selectedId, onSelect }: {
  events: ProjectActivityEvent[];
  phases: ProjectPhaseView[];
  start?: string | null;
  end?: string | null;
  today?: string | null;
  selectedId: number | null;
  onSelect: (id: number) => void;
}) {
  const dated = events.filter((event) => validDate(event.occurredAt));
  const fallback = Date.now();
  const minimum = dateValue(start) ?? (dated.length ? Math.min(...dated.map((event) => new Date(event.occurredAt).getTime())) : fallback);
  const maximum = dateValue(end) ?? (dated.length ? Math.max(...dated.map((event) => new Date(event.occurredAt).getTime())) : fallback + 86400000);
  const scale = createTimelineScale(minimum, maximum, dateValue(today) ?? fallback);
  const projectEvents = dated.filter((event) => !event.phaseName && event.phaseId == null);
  const rows = [
    { key: "project-events", name: "Project Events", phase: null, events: projectEvents },
    ...phases.map((phase) => ({
      key: phase.key,
      name: phase.name,
      phase,
      events: dated.filter((event) => event.phaseName === phase.name || String(event.phaseId ?? "") === phase.key),
    })),
  ];

  return <article className={`${styles.card} ${styles.timelineCard}`}>
    <CardHeading title="Phase Timeline" subtitle="Event markers on phase Gantt bars" action={<SeverityLegend />} />
    <TimelineHeader scale={scale} />
    <div className={styles.timelineRows}>
      {rows.map((row, index) => {
        const color = index === 0 ? "#6b7280" : PHASE_COLORS[(index - 1) % PHASE_COLORS.length];
        const plannedStart = dateValue(row.phase?.start);
        const plannedEnd = dateValue(row.phase?.end);
        const actualStart = dateValue(row.phase?.actualStart);
        const actualEnd = dateValue(row.phase?.actualEnd) ?? Math.min(scale.today, scale.maximum);
        return <div className={styles.timelineRow} key={row.key}>
          <strong title={row.name}><i style={{ background: color }} />{row.name}</strong>
          <div className={styles.timelinePlot}>
            <TimelineGrid scale={scale} />
            {plannedStart != null && plannedEnd != null && <RangeBar start={plannedStart} end={plannedEnd} scale={scale} color={color} muted />}
            {actualStart != null && <RangeBar start={actualStart} end={Math.max(actualStart, actualEnd)} scale={scale} color={color} />}
            {row.events.map((event) => <EventMarker key={event.id} event={event} scale={scale} selected={selectedId === event.id} onSelect={onSelect} offset={markerOffset(row.events, event, scale)} />)}
          </div>
          <span className={styles.rowCount}>{row.events.length || ""}</span>
        </div>;
      })}
    </div>
  </article>;
}

function LegacyActivityTimeline({
  events,
  phases: legacyPhases,
  start,
  end,
  today,
  selectedId,
  onSelect,
}: {
  events: ProjectActivityEvent[];
  phases: ProjectPhaseView[];
  start?: string | null;
  end?: string | null;
  today?: string | null;
  selectedId: number | null;
  onSelect: (id: number) => void;
}) {
  void legacyPhases;
  const dated = events.filter((event) => validDate(event.occurredAt));

  if (!dated.length) {
    return (
      <article className={`${styles.card} ${styles.timelineCard}`}>
        <CardHeading
          title="Phase Timeline"
          subtitle="Event markers on phase Gantt bars"
        />
        <div className={styles.chartEmpty}>No timeline activity.</div>
      </article>
    );
  }

  const minimum =
    dateValue(start) ??
    Math.min(
      ...dated.map((event) => new Date(event.occurredAt).getTime()),
    );

  const maximum =
    dateValue(end) ??
    Math.max(
      ...dated.map((event) => new Date(event.occurredAt).getTime()),
    );

  const todayValue = dateValue(today) ?? Date.now();

  const phases = Array.from(
    new Set(dated.map((event) => event.phaseName || "Project Events")),
  );

  const phaseColors = [
    "#6d4df6",
    "#5d65df",
    "#9064e8",
    "#df9a2f",
    "#4fa269",
    "#5274df",
    "#8457e6",
  ];

  return (
    <article className={`${styles.card} ${styles.timelineCard}`}>
      <CardHeading
        title="Phase Timeline"
        subtitle="Event markers on phase Gantt bars"
      />

      <div className={styles.timelineAxis}>
        <span>{dateMonthYear(new Date(minimum).toISOString())}</span>
        <i />
        <span>{dateMonthYear(new Date(maximum).toISOString())}</span>
      </div>

      <div className={styles.timelineRows}>
        {phases.map((phase, index) => {
          const phaseEvents = dated.filter(
            (event) => (event.phaseName || "Project Events") === phase,
          );

          const phaseTimes = phaseEvents.map((event) =>
            new Date(event.occurredAt).getTime(),
          );

          const phaseStart = Math.min(...phaseTimes);

          const completedEvent = phaseEvents.find((event) => {
            const type = String(event.eventType || "").toUpperCase();

            return (
              type.includes("COMPLETE") ||
              type.includes("ENDED") ||
              type.includes("CLOSED")
            );
          });

          const phaseEnd = completedEvent
            ? new Date(completedEvent.occurredAt).getTime()
            : Math.max(phaseStart, Math.min(todayValue, maximum));

          const left = timelinePositionFromValue(
            phaseStart,
            minimum,
            maximum,
          );

          const right = timelinePositionFromValue(
            phaseEnd,
            minimum,
            maximum,
          );

          const width = Math.max(2, right - left);

          return (
            <div className={styles.timelineRow} key={phase}>
              <strong title={phase}>{phase}</strong>

              <div className={styles.timelinePlot}>
                <i />

                <span
                  className={styles.phaseBar}
                  style={{
                    left: `${left}%`,
                    width: `${Math.min(100 - left, width)}%`,
                    background: phaseColors[index % phaseColors.length],
                  }}
                />

                {todayValue >= minimum && todayValue <= maximum && (
                  <span
                    className={styles.todayLine}
                    style={{
                      left: `${timelinePositionFromValue(
                        todayValue,
                        minimum,
                        maximum,
                      )}%`,
                    }}
                    aria-hidden="true"
                  />
                )}

                {phaseEvents.map((event) => {
                  const eventLeft = timelinePosition(
                    event.occurredAt,
                    minimum,
                    maximum,
                  );

                  return (
                    <button
                      key={event.id}
                      type="button"
                      className={`${styles.eventMarker} ${
                        styles[`severity${event.severity}`]
                      } ${
                        selectedId === event.id
                          ? styles.selectedMarker
                          : ""
                      }`}
                      style={{ left: `${eventLeft}%` }}
                      title={`${event.title} · ${dateTime(
                        event.occurredAt,
                      )}`}
                      aria-label={`${event.title}, ${dateTime(
                        event.occurredAt,
                      )}`}
                      onClick={() => onSelect(event.id)}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </article>
  );
}

void LegacyActivityTimeline;

function SwimLanes({
  events,
  start,
  end,
  today,
  selectedId,
  onSelect,
}: {
  events: ProjectActivityEvent[];
  start?: string | null;
  end?: string | null;
  today?: string | null;
  selectedId: number | null;
  onSelect: (id: number) => void;
}) {
  const dated = events
    .filter((event) => validDate(event.occurredAt))
    .sort(
      (a, b) =>
        new Date(a.occurredAt).getTime() -
        new Date(b.occurredAt).getTime(),
    );

  if (!dated.length) {
    return (
      <article className={`${styles.card} ${styles.lanesCard}`}>
        <CardHeading
          title="Swim Lanes"
          subtitle="Events by category across project weeks"
        />
        <div className={styles.chartEmpty}>No activity available.</div>
      </article>
    );
  }

  const minimum =
    dateValue(start) ??
    Math.min(
      ...dated.map((event) => new Date(event.occurredAt).getTime()),
    );

  const maximum =
    dateValue(end) ??
    Math.max(
      ...dated.map((event) => new Date(event.occurredAt).getTime()),
    );

  const todayValue = dateValue(today);
  const scale = createTimelineScale(minimum, maximum, todayValue ?? Date.now());

  const milestoneEvents = dated.filter((event) => {
    const type = String(event.eventType || "").toUpperCase();

    return (
      type.includes("MILESTONE") ||
      type.includes("SCOPE")
    );
  });

  const milestoneIds = new Set(
    milestoneEvents.map((event) => event.id),
  );

  const lanes: Array<{
    label: string;
    events: ProjectActivityEvent[];
  }> = [
    {
      label: "Milestones & Scope",
      events: milestoneEvents,
    },
    {
      label: "Phase Events",
      events: dated.filter(
        (event) =>
          event.category === "PHASES" &&
          !milestoneIds.has(event.id),
      ),
    },
    {
      label: "People",
      events: dated.filter(
        (event) => event.category === "PEOPLE",
      ),
    },
    {
      label: "Burn",
      events: dated.filter(
        (event) => event.category === "BURN",
      ),
    },
    {
      label: "Risks",
      events: dated.filter(
        (event) => event.category === "RISKS",
      ),
    },
  ];

  return (
    <article className={`${styles.card} ${styles.lanesCard}`}>
      <CardHeading
        title="Swim Lanes"
        subtitle="Events by category across project weeks"
        action={<span className={styles.dotSizeHint}>Dot size = severity</span>}
      />
      <TimelineHeader scale={scale} blankLabel />
      <div>
        {lanes.map((lane) => (
          <div className={styles.swimRow} key={lane.label}>
            <div className={styles.swimLabel}>
              <strong>{lane.label}</strong>
              <span>
                {lane.events.length}{" "}
                {lane.events.length === 1 ? "event" : "events"}
              </span>
            </div>

            <div className={styles.swimPlot}>
              <TimelineGrid scale={scale} />
              {todayValue != null &&
                todayValue >= minimum &&
                todayValue <= maximum && (
                  <span
                    className={styles.todayLine}
                    style={{
                      left: `${timelinePositionFromValue(
                        todayValue,
                        minimum,
                        maximum,
                      )}%`,
                    }}
                    aria-hidden="true"
                  />
                )}

              {lane.events.map((event) => {
                const left = timelinePosition(
                  event.occurredAt,
                  minimum,
                  maximum,
                );

                return (
                  <span key={event.id} className={styles.markerFragment}>
                  <EventMarker
                    key={event.id}
                    event={event}
                    scale={scale}
                    selected={selectedId === event.id}
                    onSelect={onSelect}
                    swim
                    legacyLeft={left}
                    offset={markerOffset(lane.events, event, scale)}
                  />
                  {/* legacy native tooltip removed
                    title={`${event.title} · ${dateTime(
                      event.occurredAt,
                    )}`}
                  */}
                  </span>
                );
              })}
            </div>

            <span className={styles.swimCount}>
              {lane.events.length}
            </span>
          </div>
        ))}
      </div>
    </article>
  );
}

function createTimelineScale(minimum: number, maximum: number, today: number): TimelineScale {
  const safeMaximum = Math.max(maximum, minimum + 86400000);
  const ticks: TimelineScale["ticks"] = [];
  const cursor = new Date(minimum);
  cursor.setDate(1);
  cursor.setMonth(cursor.getMonth() + 1);
  while (cursor.getTime() < safeMaximum) {
    ticks.push({ label: dateMonthYear(cursor.toISOString()).toUpperCase(), position: timelinePositionFromValue(cursor.getTime(), minimum, safeMaximum) });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return { minimum, maximum: safeMaximum, today, ticks };
}

function monthTickValues(start: number, end: number) {
  const ticks = [start];
  const cursor = new Date(start);
  cursor.setHours(0, 0, 0, 0);
  cursor.setDate(1);
  cursor.setMonth(cursor.getMonth() + 1);
  while (cursor.getTime() < end) {
    ticks.push(cursor.getTime());
    cursor.setMonth(cursor.getMonth() + 1);
  }
  const lastTick = new Date(ticks[ticks.length - 1]);
  const endDate = new Date(end);
  if (lastTick.getMonth() !== endDate.getMonth() || lastTick.getFullYear() !== endDate.getFullYear()) ticks.push(end);
  return ticks;
}

function TimelineHeader({ scale, blankLabel = false }: { scale: TimelineScale; blankLabel?: boolean }) {
  const labels = [{ label: dateMonthYear(new Date(scale.minimum).toISOString()).toUpperCase(), position: 0 }, ...scale.ticks];
  const todayPosition = timelinePositionFromValue(scale.today, scale.minimum, scale.maximum);
  return <div className={styles.timelineHeader}>
    <span>{blankLabel ? "" : ""}</span>
    <div className={styles.timelineHeaderPlot}>
      {labels.map((tick) => <b key={`${tick.label}-${tick.position}`} style={{ left: `${tick.position}%` }}>{tick.label}</b>)}
      {scale.today >= scale.minimum && scale.today <= scale.maximum && <em style={{ left: `${todayPosition}%` }}>Today</em>}
    </div>
    <span />
  </div>;
}

function TimelineGrid({ scale }: { scale: TimelineScale }) {
  const todayPosition = timelinePositionFromValue(scale.today, scale.minimum, scale.maximum);
  return <span className={styles.monthGrid} aria-hidden="true">
    {scale.ticks.map((tick) => <i className={styles.monthGridLine} key={tick.position} style={{ left: `${tick.position}%` }} />)}
    {scale.today >= scale.minimum && scale.today <= scale.maximum && <i className={styles.todayLine} style={{ left: `${todayPosition}%` }} />}
  </span>;
}

function RangeBar({ start, end, scale, color, muted = false }: { start: number; end: number; scale: TimelineScale; color: string; muted?: boolean }) {
  const left = timelinePositionFromValue(start, scale.minimum, scale.maximum);
  const right = timelinePositionFromValue(end, scale.minimum, scale.maximum);
  return <span className={`${styles.phaseBar} ${muted ? styles.phaseBarMuted : ""}`} style={{ left: `${left}%`, width: `${Math.max(1.5, right - left)}%`, background: color }} />;
}

function EventMarker({ event, scale, selected, onSelect, swim = false, legacyLeft, offset = 0 }: { event: ProjectActivityEvent; scale: TimelineScale; selected: boolean; onSelect: (id: number) => void; swim?: boolean; legacyLeft?: number; offset?: number }) {
  const left = legacyLeft ?? timelinePosition(event.occurredAt, scale.minimum, scale.maximum);
  const severityClass = styles[`severity${event.severity}`];
  const flip = left > 70;
  return <span className={`${styles.markerHost} ${flip ? styles.markerFlip : ""}`} style={{ left: `${left}%`, transform: `translateY(${offset}px)` }}>
    <button type="button" className={`${swim ? styles.swimDot : styles.eventMarker} ${severityClass} ${selected ? styles.selectedMarker : ""}`} aria-label={`${event.title}, ${dateTime(event.occurredAt)}`} onClick={() => onSelect(event.id)} />
    <span className={styles.markerTooltip} role="tooltip">
      <span className={styles.tooltipTop}><b><i className={severityClass} />{humanizeEventType(event.eventType)}</b><time>{dateCompact(event.occurredAt)}</time></span>
      <strong>{event.title}</strong>
      <span>{event.detail || eventSummary(event)}</span>
      <small><b className={severityClass}>{event.severity}</b> Click to highlight in list</small>
    </span>
  </span>;
}

function groupEventsByDay(events: ProjectActivityEvent[]) {
  const groups = new Map<string, ProjectActivityEvent[]>();
  events.forEach((event) => {
    const date = new Date(event.occurredAt);
    const key = Number.isNaN(date.getTime()) ? event.occurredAt : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    groups.set(key, [...(groups.get(key) ?? []), event]);
  });
  return [...groups.entries()].map(([date, groupedEvents]) => ({ date, events: groupedEvents.sort((a, b) => severityRank(b.severity) - severityRank(a.severity)) })).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

function severityRank(severity: ProjectActivityEvent["severity"]) {
  return { INFO: 1, SUCCESS: 1, WARNING: 2, CRITICAL: 3 }[severity];
}

function BurnEventMarker({ events, scale, selectedId, onSelect }: { events: ProjectActivityEvent[]; scale: TimelineScale; selectedId: number | null; onSelect: (id: number) => void }) {
  const representative = events[0];
  const left = timelinePosition(representative.occurredAt, scale.minimum, scale.maximum);
  const flip = left > 70;
  const selected = events.some((event) => event.id === selectedId);
  const severityClass = styles[`severity${representative.severity}`];
  return <span className={`${styles.markerHost} ${styles.burnMarkerHost} ${flip ? styles.markerFlip : ""}`} style={{ left: `${left}%` }}>
    <button type="button" className={`${styles.swimDot} ${severityClass} ${selected ? styles.selectedMarker : ""}`} aria-label={`${events.length} ${events.length === 1 ? "action" : "actions"} on ${dateCompact(representative.occurredAt)}`} onClick={() => onSelect(representative.id)}>
      {events.length > 1 && <span>{events.length}</span>}
    </button>
    <span className={`${styles.markerTooltip} ${styles.burnGroupTooltip}`} role="tooltip">
      <span className={styles.tooltipTop}><b><i className={severityClass} />{events.length === 1 ? eventBadgeLabel(representative) : `${events.length} actions`}</b><time>{dateCompact(representative.occurredAt)}</time></span>
      {events.length === 1 ? <>
        <strong>{representative.title}</strong>
        <span>{representative.detail || eventSummary(representative)}</span>
      </> : <span className={styles.groupedActions}>{events.map((event) => <button type="button" key={event.id} onClick={() => onSelect(event.id)}><i className={styles[`severity${event.severity}`]} /><span>{event.title}</span></button>)}</span>}
      <small><b className={severityClass}>{representative.severity}</b> Click an action to highlight it</small>
    </span>
  </span>;
}

function markerOffset(events: ProjectActivityEvent[], event: ProjectActivityEvent, scale: TimelineScale) {
  const position = timelinePosition(event.occurredAt, scale.minimum, scale.maximum);
  const nearbyBefore = events
    .filter((candidate) => candidate.id !== event.id && new Date(candidate.occurredAt).getTime() <= new Date(event.occurredAt).getTime())
    .filter((candidate) => Math.abs(timelinePosition(candidate.occurredAt, scale.minimum, scale.maximum) - position) < 2.5)
    .sort((a, b) => new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime() || a.id - b.id);
  const tier = nearbyBefore.filter((candidate) => new Date(candidate.occurredAt).getTime() < new Date(event.occurredAt).getTime() || candidate.id < event.id).length;
  return [0, -8, 8, -14, 14][tier % 5];
}

function SeverityLegend() {
  return <span className={styles.severityLegend}><i className={styles.severityCRITICAL} />Critical<i className={styles.severityWARNING} />Warning<i className={styles.severityINFO} />Info</span>;
}

function humanizeEventType(value: string) {
  return value.toLowerCase().split("_").map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ");
}

function BurnTrend({
  data,
  currency,
  events,
  selectedId,
  onSelect,
  projectStart,
  projectEnd,
}: {
  data: NonNullable<ReturnType<typeof useProjectActivity>["data"]>;
  currency: string;
  events: ProjectActivityEvent[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  projectStart: string | null;
  projectEnd: string | null;
}) {
  const usable = data.burnSeries?.length > 0;

  const todayPoint = useMemo(() => {
    if (!usable || !data.asOf) return null;

    const asOf = new Date(data.asOf).getTime();

    if (Number.isNaN(asOf)) return null;

    return [...data.burnSeries]
      .filter((point) => {
        const time = new Date(point.date).getTime();
        return !Number.isNaN(time) && time <= asOf;
      })
      .sort(
        (a, b) =>
          new Date(b.date).getTime() -
          new Date(a.date).getTime(),
      )[0] ?? null;
  }, [data.asOf, data.burnSeries, usable]);
  const chartStart = dateValue(projectStart) ?? (usable ? dateValue(data.burnSeries[0]?.date) : null);
  const chartEnd = dateValue(projectEnd) ?? (usable ? dateValue(data.burnSeries[data.burnSeries.length - 1]?.date) : null);
  const burnScale = chartStart != null && chartEnd != null ? createTimelineScale(chartStart, chartEnd, dateValue(data.asOf) ?? Date.now()) : null;
  const chartData = useMemo(() => data.burnSeries.map((point) => ({ ...point, timestamp: dateValue(point.date) })).filter((point) => point.timestamp != null && (chartStart == null || point.timestamp >= chartStart) && (chartEnd == null || point.timestamp <= chartEnd)), [chartEnd, chartStart, data.burnSeries]);
  const monthTicks = useMemo(() => chartStart != null && chartEnd != null ? monthTickValues(chartStart, chartEnd) : [], [chartEnd, chartStart]);
  const markerEvents = events.filter((event) => (event.category === "BURN" || event.category === "RISKS") && validDate(event.occurredAt));
  const markerGroups = groupEventsByDay(markerEvents);
  const projectionPercent = data.sowCeiling && data.projectedBurn != null ? (data.projectedBurn / data.sowCeiling) * 100 : null;

  return (
    <article className={`${styles.card} ${styles.burnCard}`}>
      <CardHeading
        title="Burn Trend"
        subtitle="Commercial spend accumulation · hover for values · risk markers on x-axis"
      />

      {!usable ? (
        <div className={styles.chartEmpty}>
          Burn history is unavailable for this scope.
        </div>
      ) : (
        <div className={styles.chartWrap}>
        <div className={styles.chart}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{
                top: 18,
                right: 24,
                bottom: 6,
                left: 10,
              }}
            >
              <defs>
                <linearGradient id="activityBurnFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#df8500" stopOpacity={0.14} />
                  <stop offset="100%" stopColor="#df8500" stopOpacity={0.01} />
                </linearGradient>
              </defs>
              <CartesianGrid
                stroke="#edf0f4"
                vertical
                horizontal
              />

              <XAxis
                dataKey="timestamp"
                type="number"
                scale="time"
                domain={chartStart != null && chartEnd != null ? [chartStart, chartEnd] : ["dataMin", "dataMax"]}
                ticks={monthTicks}
                tickFormatter={(value) => dateMonthYear(new Date(Number(value)).toISOString()).toUpperCase()}
                tick={{
                  fontSize: 10,
                  fill: "#8a94a8",
                }}
                axisLine={{
                  stroke: "#e5e9ef",
                }}
                tickLine={false}
              />

              <YAxis
                tickFormatter={(value) =>
                  compactCurrency(Number(value), currency)
                }
                tick={{
                  fontSize: 10,
                  fill: "#8a94a8",
                }}
                axisLine={false}
                tickLine={false}
              />

              <Tooltip
                labelFormatter={(value) =>
                  dateOnly(new Date(Number(value)).toISOString())
                }
                formatter={(value, name) => [
                  formatCurrency(
                    Number(value ?? 0),
                    currency,
                    0,
                  ),
                  name === "projectedBurn"
                    ? "Projected"
                    : "Actual burn",
                ]}
              />

              <Line
                type="monotone"
                dataKey="cumulativeBurn"
                name="Actual burn"
                stroke="#df8500"
                strokeWidth={2}
                dot={{
                  r: 3.5,
                  fill: "#ffffff",
                  stroke: "#df8500",
                  strokeWidth: 2,
                }}
                activeDot={{
                  r: 5,
                }}
                connectNulls
              />

              <Area type="monotone" dataKey="cumulativeBurn" stroke="none" fill="url(#activityBurnFill)" connectNulls legendType="none" tooltipType="none" />

              <Line
                type="monotone"
                dataKey="projectedBurn"
                name="Projected"
                stroke="#e8a94f"
                strokeWidth={1.5}
                strokeDasharray="5 4"
                dot={false}
                connectNulls
              />

              {todayPoint && (
                <ReferenceLine
                  x={dateValue(todayPoint.date) ?? undefined}
                  stroke="#e5a532"
                  strokeDasharray="4 3"
                  label={{
                    value: "Today",
                    fill: "#a86b00",
                    fontSize: 10,
                    position: "top",
                  }}
                />
              )}

              {data.sowCeiling != null && (
                <ReferenceLine
                  y={data.sowCeiling}
                  stroke="#e26565"
                  strokeDasharray="5 4"
                  label={{
                    value: "SOW",
                    fill: "#d03e3e",
                    fontSize: 10,
                    position: "right",
                  }}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {burnScale && <div className={styles.burnMarkers}>{markerGroups.map((group) => <BurnEventMarker key={group.date} events={group.events} scale={burnScale} selectedId={selectedId} onSelect={onSelect} />)}</div>}
        <div className={styles.burnFooter}>
          <span><i className={styles.actualLegend} />Actual burn <i className={styles.projectedLegend} />Projected <i className={styles.ceilingLegend} />SOW ceiling <i className={styles.severityCRITICAL} />Critical event <i className={styles.severityWARNING} />Warning event</span>
          {data.projectedBurn != null && <b>Projected end: <em>{compactCurrency(data.projectedBurn, currency)}</em>{data.sowCeiling != null && <> of {compactCurrency(data.sowCeiling, currency)} SOW ({Math.round(projectionPercent ?? 0)}%)</>}</b>}
        </div>
        </div>
      )}
    </article>
  );
}

function EventLog({
  events,
  selectedId,
  onSelect,
  hasMore,
  loadingMore,
  onLoadMore,
}: {
  events: ProjectActivityEvent[];
  selectedId: number | null;
  onSelect: (id: number | null) => void;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}) {
  const [expanded, setExpanded] = useState<Set<number>>(
    new Set(),
  );

  const refs = useRef<Record<number, HTMLElement | null>>(
    {},
  );

  useEffect(() => {
    if (selectedId != null) {
      refs.current[selectedId]?.focus({
        preventScroll: true,
      });
    }
  }, [selectedId]);

  return (
    <article className={`${styles.card} ${styles.logCard}`}>
      <CardHeading
        title="Event Log"
        action={selectedId != null ? <button type="button" className={styles.clearSelection} onClick={() => onSelect(null)}>Clear selection</button> : undefined}
        subtitle={`${events.length} events · click a chart marker to highlight`}
      />

      <div className={styles.eventLog}>
        {events.map((event) => {
          const open = expanded.has(event.id);

          return (
            <article
              id={`activity-event-${event.id}`}
              tabIndex={0}
              ref={(node) => {
                refs.current[event.id] = node;
              }}
              key={event.id}
              className={`${styles.logEvent} ${
                selectedId === event.id
                  ? styles.logEventSelected
                  : ""
              }`}
              onClick={() => onSelect(event.id)}
            >
              <span
                className={`${styles.logIcon} ${
                  styles[`severity${event.severity}`]
                }`}
              >
                {eventIcon(event)}
              </span>

              <div className={styles.logContent}>
                <div>
                  <strong>{event.title}</strong>

                </div>

                <div className={styles.eventBadgeRow}>
                  <span className={`${styles.eventTypeBadge} ${styles[`badge${eventBadgeTone(event)}`]}`}>{eventBadgeLabel(event)}</span>
                  {(event.phaseName || event.workstreamName) && <span className={styles.eventContext}><i className={styles[`category${event.category}`]} />{event.phaseName || event.workstreamName}</span>}
                </div>

                {(selectedId === event.id || open) && <p>{event.detail || eventSummary(event)}</p>}

                {open && (
                  <dl className={styles.eventDetails}>
                    <div>
                      <dt>Occurred</dt>
                      <dd>{dateTime(event.occurredAt)}</dd>
                    </div>

                    {event.workstreamName && (
                      <div>
                        <dt>Workstream</dt>
                        <dd>{event.workstreamName}</dd>
                      </div>
                    )}

                    {event.phaseName && (
                      <div>
                        <dt>Phase</dt>
                        <dd>{event.phaseName}</dd>
                      </div>
                    )}

                    {event.employeeName && (
                      <div>
                        <dt>Employee</dt>
                        <dd>{event.employeeName}</dd>
                      </div>
                    )}

                    {event.actorName && (
                      <div>
                        <dt>Changed by</dt>
                        <dd>{event.actorName}</dd>
                      </div>
                    )}
                  </dl>
                )}
              </div>

              <div className={styles.logMeta}>
                <time>{dateCompact(event.occurredAt)}</time>

                <span>{categoryLabel(event.category)}</span>

                <button
                  type="button"
                  aria-label={`${
                    open ? "Collapse" : "Expand"
                  } ${event.title}`}
                  onClick={(click) => {
                    click.stopPropagation();

                    setExpanded((current) => {
                      const next = new Set(current);

                      if (open) {
                        next.delete(event.id);
                      } else {
                        next.add(event.id);
                      }

                      return next;
                    });
                  }}
                >
                  {open ? <ChevronUp /> : <ChevronDown />}
                </button>
              </div>
            </article>
          );
        })}
      </div>

      {hasMore && (
        <footer className={styles.loadMore}>
          <Button
            size="sm"
            disabled={loadingMore}
            onClick={onLoadMore}
          >
            {loadingMore ? (
              <>
                <Spinner variant="inline" />
                Loading...
              </>
            ) : (
              "Load more"
            )}
          </Button>
        </footer>
      )}
    </article>
  );
}

function CardHeading({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle: string;
  action?: React.ReactNode;
}) {
  return (
    <header className={styles.cardHeading}>
      <div>
        <h3>{title}</h3>
        <p>{subtitle}</p>
      </div>
      {action}
    </header>
  );
}

function State({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone?: "error";
}) {
  return (
    <div
      className={`${styles.state} ${
        tone === "error" ? styles.stateError : ""
      }`}
    >
      {children}
    </div>
  );
}

function validDate(value?: string | null) {
  return (
    value != null &&
    !Number.isNaN(new Date(value).getTime())
  );
}

function deduplicateActivityEvents(events: ProjectActivityEvent[]) {
  const unique = new Map<string, ProjectActivityEvent>();
  events.forEach((event) => {
    const occurred = new Date(event.occurredAt);
    const day = Number.isNaN(occurred.getTime())
      ? String(event.occurredAt)
      : `${occurred.getFullYear()}-${occurred.getMonth() + 1}-${occurred.getDate()}`;
    const title = String(event.title || "").trim().toLowerCase().replace(/\s+/g, " ");
    const key = [
      event.projectId,
      event.workstreamId ?? "project",
      event.phaseId ?? event.phaseName ?? "no-phase",
      event.employeeId ?? event.employeeName ?? "no-member",
      event.eventType,
      day,
      title,
    ].join("|");
    const existing = unique.get(key);
    if (!existing || (existing.reconstructed && !event.reconstructed)) unique.set(key, event);
  });
  return [...unique.values()];
}

function dateValue(value?: string | null) {
  return validDate(value)
    ? new Date(value!).getTime()
    : null;
}

function timelinePosition(
  date: string,
  start: number,
  end: number,
) {
  const value = new Date(date).getTime();

  if (Number.isNaN(value)) {
    return 0;
  }

  return timelinePositionFromValue(value, start, end);
}

function timelinePositionFromValue(
  value: number,
  start: number,
  end: number,
) {
  const span = Math.max(end - start, 86400000);

  return Math.max(
    0,
    Math.min(100, ((value - start) / span) * 100),
  );
}

function dateOnly(value: string) {
  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(date);
}

function dateCompact(value: string) {
  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "2-digit",
      }).format(date);
}

function dateMonthYear(value: string) {
  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("en-US", {
        month: "short",
        year: "2-digit",
      }).format(date);
}

function dateTime(value: string) {
  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }).format(date);
}

function categoryLabel(
  category: ProjectActivityCategory,
) {
  return (
    {
      PHASES: "Phases",
      PEOPLE: "People",
      BURN: "Burn",
      RISKS: "Risks",
    } as const
  )[category];
}

function eventBadgeLabel(event: ProjectActivityEvent) {
  const labels: Record<string, string> = {
    PHASE_STARTED: "Phase Start",
    PHASE_RISK: "Phase Risk",
    MEMBER_ALLOCATED: "Allocation",
    MEMBER_DEALLOCATED: "Deallocation",
    CONFIGURATION_CHANGED: "Scope Change",
    SCOPE_CHANGED: "Scope Change",
    BURN_THRESHOLD_CROSSED: "Burn Milestone",
    PROJECT_STATUS_CHANGED: "Status Change",
  };
  return labels[event.eventType] ?? humanizeEventType(event.eventType);
}

function eventBadgeTone(event: ProjectActivityEvent) {
  if (event.eventType === "BURN_THRESHOLD_CROSSED") return "Burn";
  if (event.eventType === "MEMBER_ALLOCATED") return "Allocation";
  if (event.eventType === "MEMBER_DEALLOCATED") return "People";
  if (event.eventType === "PHASE_STARTED") return "Start";
  if (event.eventType === "PHASE_RISK") return "Risk";
  return "Scope";
}

function compactCurrency(
  value: number,
  currency: string,
) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency || "INR",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

function eventIcon(event: ProjectActivityEvent) {
  if (event.category === "PEOPLE") {
    return <Users />;
  }

  if (event.category === "RISKS") {
    return <AlertTriangle />;
  }

  if (event.category === "BURN") {
    return event.severity === "CRITICAL" ? (
      <ArrowUp />
    ) : (
      <ArrowDown />
    );
  }

  if (event.eventType === "PHASE_STARTED") {
    return <CheckCircle2 />;
  }

  return <RefreshCw />;
}

function eventSummary(event: ProjectActivityEvent) {
  return (
    [
      event.phaseName,
      event.workstreamName,
      event.employeeName,
    ]
      .filter(Boolean)
      .join(" · ") || "Project-level change"
  );
}
